/**
 * Kimyo stoli (bench) laboratoriya ishi — JSON sxema.
 * NOBOOK uslubi: reaktivlar javoni, idishlar, asboblar, erkin tajriba; topshiriqlar
 * kuzatuvlar/o'lchovlar bo'yicha avtomatik tekshiriladi.
 */
import { z } from 'zod';
import { I18nText, DisplayTransform, Quiz, VariantParam, Reference } from '../scenario/schema';

export const ShelfItem = z
  .object({
    id: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]*$/),
    /** Modda id (species) — "$var" bo'lishi mumkin (noma'lum modda) */
    species: z.string(),
    form: z.enum(['solid', 'solution', 'liquid', 'strip', 'indicator', 'gas']),
    /** mol/L (solution/indicator); "$var" — variantdan (noma'lum konsentratsiya) */
    molarity: z.union([z.number().min(0), z.string().regex(/^\$[a-zA-Z_][a-zA-Z0-9_]*$/)]).optional(),
    /** Javondagi yorliq (yo'q bo'lsa modda nomi); noma'lum modda uchun "X eritmasi" */
    label: I18nText.optional(),
    /** Haqiqiy moddani yashirish (noma'lumni aniqlash topshiriqlari) */
    hidden: z.boolean().default(false),
    /** Bir marta qo'shish miqdori va chegarasi: g (solid/strip), mL (solution/liquid/gas), tomchi (indicator) */
    defaultAmount: z.number().positive(),
    maxAmount: z.number().positive(),
    step: z.number().positive().default(1),
  })
  .strict();
export type ShelfItem = z.infer<typeof ShelfItem>;

export const Glassware = z
  .object({
    id: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]*$/),
    type: z.enum(['testTube', 'beaker', 'flask', 'dish', 'crucible', 'conical', 'roundFlask', 'gasJar', 'bigTube']),
    capacityMl: z.number().positive(),
    label: I18nText.optional(),
  })
  .strict();
export type Glassware = z.infer<typeof Glassware>;

export const BenchTool = z.enum(['burner', 'pHMeter', 'thermometer', 'balance', 'splint', 'gasTube', 'filter']);
export type BenchTool = z.infer<typeof BenchTool>;

/** Topshiriq tekshiruvi */
export const BenchCheck = z.discriminatedUnion('type', [
  z
    .object({
      type: z.literal('observation'),
      /** obs.precipitate, obs.gasStart, obs.colorChange, obs.deposit, obs.heatReleased, obs.dissolved, obs.boiling ... */
      kind: z.string(),
      species: z.string().optional(),
      vessel: z.string().optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal('measurement'),
      tool: z.enum(['pH', 'T', 'mass']),
      min: z.number().optional(),
      max: z.number().optional(),
      vessel: z.string().optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal('species'),
      /** Idishda shu modda (qattiq/erigan/ion ko'rinishida) kamida shuncha mol */
      species: z.string(),
      minMol: z.number().positive().default(1e-5),
      vessel: z.string().optional(),
    })
    .strict(),
  z.object({ type: z.literal('splint'), result: z.enum(['pop', 'relight', 'extinguish', 'burns']) }).strict(),
  z.object({ type: z.literal('manual') }).strict(),
]);
export type BenchCheck = z.infer<typeof BenchCheck>;

export const BenchTask = z
  .object({
    id: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]*$/),
    text: I18nText,
    hint: I18nText.optional(),
    check: BenchCheck,
    points: z.number().min(0).default(0),
  })
  .strict();
export type BenchTask = z.infer<typeof BenchTask>;

export const BenchReportField = z
  .object({
    id: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]*$/),
    label: I18nText,
    hint: I18nText.optional(),
    display: DisplayTransform.default({}),
    kind: z.enum(['number', 'choice']).default('number'),
    /** choice uchun variantlar (qiymat = indeks yoki satr) */
    options: z.array(z.object({ value: z.union([z.number(), z.string()]), label: I18nText }).strict()).optional(),
    reference: Reference,
    tolerance: z.object({ rel: z.number().min(0).optional(), abs: z.number().min(0).optional() }).strict().default({}),
    points: z.number().min(0),
  })
  .strict();
export type BenchReportField = z.infer<typeof BenchReportField>;

/** Namoyish tajribasining bir qadami: harakat (t siz) + izoh; `wait` — oldingi qadamdan keyin, s */
export const BenchDemoStep = z
  .object({
    wait: z.number().min(0).default(1),
    caption: I18nText.optional(),
    /** BenchAction (t maydonisiz): {type:'addVessel', ...}, {type:'add', ...}, ... */
    action: z.record(z.unknown()),
  })
  .strict();
export type BenchDemoStep = z.infer<typeof BenchDemoStep>;

/** Namoyish (o'qituvchi tajribasi): stolda avtomatik bajariladigan harakatlar ketma-ketligi */
export const BenchDemo = z
  .object({
    id: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_-]*$/),
    title: I18nText,
    description: I18nText,
    /** Bo'lim: acid-base, redox, precipitation, gas, organic, ... */
    category: z.string().default('general'),
    /** Reaksiya tenglamasi (ko'rsatish uchun) */
    equation: z.string().optional(),
    steps: z.array(BenchDemoStep).min(1),
  })
  .strict();
export type BenchDemo = z.infer<typeof BenchDemo>;

export const BenchLab = z
  .object({
    kind: z.literal('bench'),
    id: z.string().regex(/^[a-z][a-z0-9-]*$/),
    version: z.number().int().min(1),
    title: I18nText,
    subject: I18nText,
    objective: I18nText,
    theory: I18nText,
    /** Umumiy ko'rsatmalar (qadamlar topshiriqlarda) */
    procedure: z.array(I18nText).min(1),
    shelf: z.array(ShelfItem).min(1),
    glassware: z.array(Glassware).default([]),
    tools: z.array(BenchTool).default(['burner', 'pHMeter', 'thermometer', 'balance', 'splint', 'gasTube', 'filter']),
    variant: z.record(VariantParam).default({}),
    tasks: z.array(BenchTask).min(1),
    demos: z.array(BenchDemo).default([]),
    quiz: Quiz.optional(),
    report: z
      .object({
        fields: z.array(BenchReportField).default([]),
        conclusion: z
          .object({ required: z.boolean().default(true), points: z.number().min(0).default(0) })
          .strict()
          .default({}),
      })
      .strict()
      .default({}),
    penalties: z
      .object({ hintPenalty: z.number().min(0).default(0) })
      .strict()
      .default({}),
    grading: z.object({ passFraction: z.number().min(0).max(1).default(0.6) }).strict().default({}),
    simulation: z
      .object({ dt: z.number().positive().default(1), tickMs: z.number().positive().default(500) })
      .strict()
      .default({}),
  })
  .strict();
export type BenchLab = z.infer<typeof BenchLab>;

export function parseBenchLab(json: unknown): BenchLab {
  return BenchLab.parse(json);
}

export function benchMaxScore(lab: BenchLab): number {
  const tasks = lab.tasks.reduce((s, t) => s + t.points, 0);
  const fields = lab.report.fields.reduce((s, f) => s + f.points, 0);
  const quiz = lab.quiz?.questions.reduce((s, q) => s + q.points, 0) ?? 0;
  return tasks + fields + quiz + lab.report.conclusion.points;
}

/** Mijozga: bilim testi to'g'ri javoblari olib tashlanadi (yashirin moddalar simulyatsiya uchun qoladi, UI yorlig'ini ko'rsatadi) */
export function publicBenchLab(lab: BenchLab): BenchLab {
  if (!lab.quiz) return lab;
  return { ...lab, quiz: { ...lab.quiz, questions: lab.quiz.questions.map(({ correct: _c, explanation: _e, ...q }) => q) } };
}
